import { hasTenantProduct } from '@stoaria-control/schema'
import type { TenantProduct } from '@stoaria-control/schema'
import type { TreeNode } from './hierarchy-api.ts'

/**
 * The jumps between a project and the products that hang off it, as data.
 *
 * A project node is one row in `org_nodes`, and three screens address it:
 * Projects edits it, Vault stores items on it, Sessions runs agents in it.
 * Before this an operator had to re-find the same node by hand on each —
 * and on Vault that means re-reading a path, which CLAUDE.md records an
 * hour lost to (2026-10-03).
 *
 * **The coupling is one-directional and it travels in the URL.** Projects
 * links to `/vault/projects?node=<id>` and `/sessions?projectId=<id>`;
 * Vault links back to `/projects?sel=<id>&selKind=<kind>`. Neither page
 * imports the other's modules, and this module imports neither — it is
 * pure decisions over plain data, which is why the rules below can be
 * tested without a router or a fetch.
 *
 * Why not render the `<Link>`s here: TanStack's `<Link to>` is checked
 * against the generated route tree, and a destination carried as
 * `Record<string, string>` throws that away. So this module decides WHICH
 * jumps exist and WHAT they say, and the components write the literal
 * `to`/`search` the router can check.
 */

/**
 * How the segments of a node path are joined, everywhere one is shown.
 *
 * One definition because the three places that render a path have to
 * produce the SAME string: the vault tree's hover text, the vault
 * toolbar's copyable path, and the line under the Projects page's "Open
 * vault" link. An operator compares those by eye against the `-p …` they
 * are about to type, and a separator that differs by a space makes two
 * identical paths look different.
 */
export const NODE_PATH_SEPARATOR = ' / '

/** A node path from an ancestor chain, root first and including the node
 * itself — the shape `findInTree` returns as `ancestors`. */
export function nodePathOf(ancestors: readonly { name: string }[]): string {
  return ancestors.map((ancestor) => ancestor.name).join(NODE_PATH_SEPARATOR)
}

/** Which jump this is. The component switches on it to write a typed
 * destination; nothing else should branch on it. */
export type ProjectJumpKind = 'vault' | 'sessions'

export interface ProjectJump {
  kind: ProjectJumpKind
  label: string
  /**
   * One short line under the link, so the jump is informed rather than
   * blind: how many live sessions are waiting, or which vault node the
   * link is about to select.
   *
   * `null` when the number behind it could not be had. A link with no
   * count still works; a link showing a wrong or stale count is worse
   * than one showing none.
   */
  context: string | null
}

export interface ProjectJumpsInput {
  /**
   * The tenant's product set, by way of `lib/tenant-products.ts`, or
   * `null` for "could not find out" — the same vocabulary, and the same
   * fail-open rule, as `navForProducts` in `nav.ts`. See `projectJumps`.
   */
  products: readonly TenantProduct[] | null
  /** The selected node's path, root first — what the vault page will
   * select, and the string an operator retypes as `-p …`. */
  nodePath: string
  /**
   * Live sessions for this project, or `null` when the count was not
   * fetched (no entitlement, a selection that is not a project, or a
   * failed request — all of which render the link bare).
   */
  liveSessionCount: number | null
}

/**
 * The jumps to offer from a project, in the order they are shown.
 *
 * A jump the tenant has not bought is ABSENT, not disabled: the API
 * refuses an unentitled tenant's `/vault/**` and `/sessions**` calls with
 * the same 403 a non-member gets, so offering a greyed control would be
 * promising something the server will not serve. Same rule, and the same
 * reasoning, as `navForProducts` in `nav.ts` — this is the sidebar's
 * decision applied to a link in the page body, and it is cosmetic
 * agreement with the server rather than the control itself.
 *
 * `products === null` is "unknown", not "bought nothing", and it offers
 * both jumps. Failing open is right for the same reason it is right in
 * the sidebar: a link that should not be there costs one 403 when it is
 * clicked, while a link wrongly hidden by a `/hierarchy` blip reads as an
 * account that has lost a product.
 */
export function projectJumps({
  products,
  nodePath,
  liveSessionCount,
}: ProjectJumpsInput): ProjectJump[] {
  const jumps: ProjectJump[] = []
  if (isEntitled(products, 'vault'))
    jumps.push({
      kind: 'vault',
      label: 'Open vault for this project',
      // The path, not a count of items. A per-node item/approval count
      // would mean calling `/vault/admin/**` from the Projects page, which
      // is exactly the module coupling this file exists to avoid — see the
      // PR's `problems`. The path is the fact an operator actually checks
      // before trusting a vault screen, and it is already in hand here.
      context: nodePath === '' ? null : nodePath,
    })
  if (isEntitled(products, 'sessions'))
    jumps.push({
      kind: 'sessions',
      label: 'Sessions for this project',
      context: liveSessionsLabel(liveSessionCount),
    })
  return jumps
}

/** `hasTenantProduct` with the chrome's unknown-is-open rule in front of
 * it. Not exported: "unknown counts as entitled" is a rendering decision
 * and must never become an answer anything else trusts. */
function isEntitled(
  products: readonly TenantProduct[] | null,
  product: TenantProduct,
): boolean {
  return products === null || hasTenantProduct(products, product)
}

/** "3 live sessions" / "1 live session" / "No live sessions", or `null`
 * when the count is unknown. Spelled out rather than a bare number, since
 * the line sits on its own under a link and "0" alone reads as a
 * rendering failure. */
export function liveSessionsLabel(count: number | null): string | null {
  if (count === null) return null
  if (count === 0) return 'No live sessions'
  return `${String(count)} live session${count === 1 ? '' : 's'}`
}

/**
 * The context under Vault's "Open in Projects" link: how much of the org
 * tree hangs off this node.
 *
 * It answers the question that decides whether the jump is worth making —
 * whether Projects is about to open a leaf you can edit or a branch you
 * will have to walk down. Derived from the hierarchy the vault page
 * already loaded, so the link costs no extra request and pulls no projects
 * module into the vault page.
 */
export function nodeScopeLabel(descendantCount: number): string {
  if (descendantCount === 0) return 'No nodes beneath it'
  return `${String(descendantCount)} node${
    descendantCount === 1 ? '' : 's'
  } beneath it`
}

/** Every node under `node`, not counting `node` itself. */
export function countDescendants(node: TreeNode): number {
  return node.children.reduce(
    (total, child) => total + 1 + countDescendants(child),
    0,
  )
}
